import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { isProd } from '../../../../../../config/config';
import getStripePromise from '../../../../utils/stripe';
import { stripeAppearance, stripeFonts } from '../../../../config/stripe';
import { normalizeUsPhoneDigits } from '../../../../utils/addresses';

import './SubscriptionUnlimitedElementsCheckout.scss';

const stripePromise = getStripePromise();

/**
 * E.164 placeholder when we do not collect phone; required if Element billingDetails are all
 * `never`.
 */
const STRIPE_BILLING_PHONE_PLACEHOLDER = '+12025550100';
const NON_US_WALLET_ERROR_MESSAGE = 'Trashie Unlimited is currently available only for U.S. shipping addresses. Please use a U.S. address or checkout with card.';

/** ISO2/ISO3/numeric/labels from Stripe / Apple Pay — not always literally "US". */
function isExpressCheckoutUsCountry(countryRaw) {
  if (countryRaw == null || countryRaw === '') {
    return false;
  }
  const s = String(countryRaw).trim();
  if (!s) {
    return false;
  }
  const upper = s.toUpperCase();
  if (upper === 'US' || upper === 'USA') {
    return true;
  }
  if (upper === '840') {
    return true;
  }
  const compact = upper.replace(/\./g, '').replace(/\s+/g, '');
  if (compact === 'US' || compact === 'USA') {
    return true;
  }
  const lower = s.toLowerCase();
  return lower === 'united states' || lower === 'united states of america';
}

/**
 * Stripe Appearance API ignores display:none.
 * fontSize 0 hides label text only; placeholders remain on .Input.
 */
function readStripeExpressDebugFromUrl() {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const v = new URLSearchParams(window.location.search).get('stripeExpressDebug');
    return v === '1' || v === 'true';
  } catch {
    return false;
  }
}

/** Console filter: [Trashie][Express Checkout] */
const EXPRESS_LOG_PREFIX = '[Trashie][Express Checkout]';

function shouldLogExpressDiagnostics() {
  if (readStripeExpressDebugFromUrl()) {
    return true;
  }
  const env = typeof process !== 'undefined' ? process.env?.REACT_APP_ENV : undefined;
  return (
    env === 'qa' ||
    env === 'staging' ||
    env === 'development' ||
    env === 'local' ||
    !isProd()
  );
}

function logExpress(level, title, detail = {}) {
  const payload = {
    reactAppEnv: typeof process !== 'undefined' ? process.env?.REACT_APP_ENV : undefined,
    isProd: isProd(),
    ...detail,
  };
  const args = [EXPRESS_LOG_PREFIX, title, payload];
  if (level === 'warn') {
    console.warn(...args);
  } else if (level === 'error') {
    console.error(...args);
  } else {
    console.info(...args);
  }
}

function shouldLogGooglePayExpressDiagnostics() {
  return shouldLogExpressDiagnostics();
}
function logGooglePayExpress(level, title, detail = {}) {
  return logExpress(level, title, detail);
}

function readGooglePaySignalsFromWindow() {
  if (typeof window === 'undefined') {
    return {};
  }
  return {
    origin: window.location?.origin,
    protocol: window.location?.protocol,
    isSecureContext: window.isSecureContext,
    hasPaymentRequestApi: typeof window.PaymentRequest === 'function',
  };
}

function parseGooglePayFromExpressMethods(methods) {
  const m = methods && typeof methods === 'object' ? methods : {};
  const googlePay = Boolean(m.googlePay ?? m.google_pay);
  const applePay = Boolean(m.applePay ?? m.apple_pay);
  const link = Boolean(m.link);
  return {
    rawKeys: Object.keys(m),
    googlePay,
    applePay,
    link,
    anyWallet: googlePay || applePay || link,
  };
}

const STRIPE_LABEL_TEXT_HIDDEN = {
  fontSize: '0px',
  lineHeight: '0',
  margin: '0',
  marginTop: '0',
  marginBottom: '0',
  padding: '0',
  paddingTop: '0',
  paddingBottom: '0',
  transition: 'none',
};

const ElementsInnerForm = ({
  disabled = false,
  checkoutFormReady = true,
  email,
  billingCountryCode = 'US',
  billingPostalCode = '',
  billingAddressLine1 = '',
  billingAddressLine2 = '',
  billingCity = '',
  billingState = '',
  /** Raw phone from profile or checkout when available (not shown in UI if empty). */
  billingPhoneRaw = '',
  cardholderName = '',
  onBillingCountryCodeChange,
  onBillingPostalCodeChange,
  onCardholderNameChange,
  onError,
  onSuccess,
  returnUrl,
  expressPortalRef,
}) => {
  const payBlocked = disabled || !checkoutFormReady;
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [availableWallets, setAvailableWallets] = useState(null);
  const [expressLoadError, setExpressLoadError] = useState(null);
  const diagnosticsEnabled = useMemo(() => shouldLogGooglePayExpressDiagnostics(), []);
  const expressResolved = availableWallets != null;
  const hasExpressWallets =
    expressResolved && Object.values(availableWallets).some((enabled) => Boolean(enabled));
  /* In-flow Express until onReady; off-screen probing often breaks Google Pay. */
  const showExpressSection = !expressResolved || hasExpressWallets;

  useEffect(() => {
    if (!diagnosticsEnabled) {
      return;
    }
    logGooglePayExpress('info', 'Session fingerprint (Google Pay needs HTTPS / secure context in most browsers)', {
      ...readGooglePaySignalsFromWindow(),
      userAgentSnippet:
        typeof navigator !== 'undefined' ? String(navigator.userAgent).slice(0, 160) : null,
    });
  }, [diagnosticsEnabled]);

  useEffect(() => {
    if (!diagnosticsEnabled) {
      return;
    }
    const detail = {
      hint: 'Filter console by [Trashie][Google Pay / Express]. Production: add ?stripeExpressDebug=1',
      ...readGooglePaySignalsFromWindow(),
      userAgentSnippet:
        typeof navigator !== 'undefined' ? String(navigator.userAgent).slice(0, 120) : null,
      stripeLoaded: Boolean(stripe),
      elementsLoaded: Boolean(elements),
      disabledParent: disabled,
      payBlocked,
      checkoutFormReady,
      expressColumnShouldMount: Boolean(showExpressSection && paymentElementReady),
      expressResolved,
      hasExpressWallets,
      showExpressSection,
      paymentElementReady,
      availablePaymentMethods:
        availableWallets == null ? 'pending (awaiting ExpressCheckoutElement onReady)' : availableWallets,
      parsedWallets: availableWallets ? parseGooglePayFromExpressMethods(availableWallets) : null,
      expressLoadError,
    };
    logGooglePayExpress('info', 'Express / PaymentElement state snapshot', detail);
  }, [
    diagnosticsEnabled,
    stripe,
    elements,
    disabled,
    payBlocked,
    checkoutFormReady,
    expressResolved,
    hasExpressWallets,
    showExpressSection,
    availableWallets,
    expressLoadError,
    paymentElementReady,
  ]);
  useEffect(() => {
    if (!diagnosticsEnabled || !paymentElementReady) {
      return undefined;
    }
    const t = window.setTimeout(() => {
      if (availableWallets == null) {
        logGooglePayExpress('warn', 'ExpressCheckoutElement onReady still pending after 8s — Google Pay button will not render until Stripe reports wallets', {
          paymentElementReady,
          expressLoadError,
        });
      }
    }, 8000);
    return () => window.clearTimeout(t);
  }, [diagnosticsEnabled, paymentElementReady, availableWallets, expressLoadError]);
  const expressOptions = useMemo(() => ({
    buttonHeight: 44,
    layout: {
      maxColumns: 2,
      maxRows: 1,
      overflow: 'auto',
    },
    paymentMethods: {
      googlePay: 'auto',
      applePay: 'auto',
      link: 'auto',
    },
  }), []);
  /*
   * Billing details from our form via confirmParams (like embedded Checkout shipping).
   */
  const paymentElementOptions = useMemo(() => ({
    paymentMethodOrder: ['card'],
    layout: 'auto',
    fields: {
      billingDetails: 'never',
    },
    terms: {
      card: 'never',
    },
    /* Google/Apple Pay: use ExpressCheckoutElement only, not Payment Element tabs */
    wallets: {
      applePay: 'never',
      googlePay: 'never',
    },
  }), []);

  const billingDetailsForConfirm = useMemo(() => {
    const phoneDigits = normalizeUsPhoneDigits(billingPhoneRaw || '');
    const phone = phoneDigits ? `+1${phoneDigits}` : STRIPE_BILLING_PHONE_PLACEHOLDER;

    const state = (billingState || '').trim();
    const postal = (billingPostalCode || '').trim();
    const line1 = (billingAddressLine1 || '').trim();
    const line2 = (billingAddressLine2 || '').trim();
    const city = (billingCity || '').trim();
    const country = billingCountryCode || 'US';

    return {
      ...(email ? { email } : {}),
      ...(cardholderName.trim() ? { name: cardholderName.trim() } : {}),
      phone,
      address: {
        country,
        ...(line1 ? { line1 } : {}),
        ...(line2 ? { line2 } : {}),
        ...(city ? { city } : {}),
        ...(state ? { state } : {}),
        ...(postal ? { postal_code: postal } : {}),
      },
    };
  }, [
    email,
    cardholderName,
    billingPhoneRaw,
    billingCountryCode,
    billingPostalCode,
    billingAddressLine1,
    billingAddressLine2,
    billingCity,
    billingState,
  ]);

  const confirmAndFinalize = async (expressEvent) => {
    const isExpress = !!expressEvent;
    const expressWalletType = expressEvent?.expressPaymentType || 'unknown';

    if (!stripe || !elements || isSubmitting) {
      logExpress('warn', 'confirmAndFinalize — early exit (stripe/elements/isSubmitting)', {
        stripeLoaded: Boolean(stripe),
        elementsLoaded: Boolean(elements),
        isSubmitting,
        isExpress,
      });
      return;
    }
    if (!isExpress && payBlocked) {
      return;
    }

    if (isExpress) {
      logExpress('info', `Express ${expressWalletType} onConfirm — starting confirmAndFinalize`, {
        walletType: expressWalletType,
        hasBillingDetails: Boolean(expressEvent?.billingDetails),
        hasShippingAddress: Boolean(expressEvent?.shippingAddress),
        billingEmail: expressEvent?.billingDetails?.email || '(none)',
        billingName: expressEvent?.billingDetails?.name || '(none)',
        shippingName: expressEvent?.shippingAddress?.name || '(none)',
        shippingCountry: expressEvent?.shippingAddress?.address?.country || '(none)',
        shippingState: expressEvent?.shippingAddress?.address?.state || '(none)',
        eventKeys: Object.keys(expressEvent || {}),
      });

      const expressCountry =
        expressEvent?.shippingAddress?.address?.country ||
        expressEvent?.billingDetails?.address?.country ||
        '';
      if (expressCountry.trim() && !isExpressCheckoutUsCountry(expressCountry)) {
        logExpress('warn', `Express ${expressWalletType} — rejected non-US country`, { expressCountry });
        setErrorMessage(NON_US_WALLET_ERROR_MESSAGE);
        onError?.(NON_US_WALLET_ERROR_MESSAGE);
        return;
      }
    }

    setIsSubmitting(true);
    setErrorMessage('');

    if (isExpress) {
      logExpress('info', `Express ${expressWalletType} — calling elements.submit()`, {});
    }
    const { error: submitError } = await elements.submit();
    if (submitError) {
      logExpress('error', `Express ${expressWalletType} — elements.submit() FAILED`, {
        errorType: submitError.type,
        errorCode: submitError.code,
        errorMessage: submitError.message,
        errorDeclineCode: submitError.decline_code,
      });
      const message = submitError.message || 'Payment details are incomplete.';
      setErrorMessage(message);
      onError?.(message);
      setIsSubmitting(false);
      return;
    }

    if (isExpress) {
      logExpress('info', `Express ${expressWalletType} — elements.submit() OK, calling stripe.confirmPayment()`, {});
    }

    let confirmResult;
    try {
      confirmResult = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: returnUrl,
          ...(!isExpress ? {
            payment_method_data: {
              billing_details: billingDetailsForConfirm,
            },
          } : {}),
        },
      });
    } catch (confirmException) {
      logExpress('error', `Express ${expressWalletType} — stripe.confirmPayment() THREW exception`, {
        name: confirmException?.name,
        message: confirmException?.message,
        stack: confirmException?.stack?.slice(0, 500),
      });
      const message = confirmException?.message || 'Payment could not be completed.';
      setErrorMessage(message);
      onError?.(message);
      setIsSubmitting(false);
      return;
    }

    const { error, paymentIntent } = confirmResult;

    if (isExpress) {
      logExpress(
        error ? 'error' : 'info',
        `Express ${expressWalletType} — stripe.confirmPayment() returned`,
        {
          hasError: Boolean(error),
          errorType: error?.type,
          errorCode: error?.code,
          errorMessage: error?.message,
          errorDeclineCode: error?.decline_code,
          errorPaymentIntentStatus: error?.payment_intent?.status,
          paymentIntentId: paymentIntent?.id || error?.payment_intent?.id || null,
          paymentIntentStatus: paymentIntent?.status,
          paymentMethodId: typeof paymentIntent?.payment_method === 'string'
            ? paymentIntent.payment_method
            : paymentIntent?.payment_method?.id || null,
        },
      );
    }

    let expressDetails = null;
    if (isExpress) {
      const bd = expressEvent?.billingDetails || {};
      const sa = expressEvent?.shippingAddress || {};
      const addr = sa?.address || bd?.address || {};
      const nameParts = (sa?.name || bd?.name || '').split(' ');
      expressDetails = {
        email: bd?.email || '',
        phone: bd?.phone || '',
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        address1: addr?.line1 || '',
        address2: addr?.line2 || '',
        city: addr?.city || '',
        state: addr?.state || '',
        zip: addr?.postal_code || '',
        countryCode: addr?.country || 'US',
      };
      logExpress('info', `Express ${expressWalletType} — parsed expressDetails for finalize`, {
        hasEmail: Boolean(expressDetails.email),
        hasFirstName: Boolean(expressDetails.firstName),
        hasLastName: Boolean(expressDetails.lastName),
        hasAddress1: Boolean(expressDetails.address1),
        hasCity: Boolean(expressDetails.city),
        hasState: Boolean(expressDetails.state),
        hasZip: Boolean(expressDetails.zip),
        countryCode: expressDetails.countryCode,
      });
    }

    if (error) {
      if (
        error?.code === 'payment_intent_unexpected_state' &&
        error?.payment_intent?.status === 'succeeded'
      ) {
        logExpress('info', `Express ${expressWalletType} — PI already succeeded (idempotent), calling onSuccess`, {});
        try {
          await onSuccess?.(error.payment_intent.id, expressDetails);
        } catch (finalizeErr) {
          const finalizeMessage =
            finalizeErr && typeof finalizeErr.message === 'string' && finalizeErr.message.trim()
              ? finalizeErr.message
              : 'Your payment was successful but we could not activate your membership yet.';
          logExpress('error', `Express ${expressWalletType} — onSuccess threw after idempotent PI`, { finalizeMessage });
          setErrorMessage(finalizeMessage);
          onError?.(finalizeMessage);
        }
        setIsSubmitting(false);
        return;
      }

      const message = error.message || 'Payment could not be completed.';
      logExpress('error', `Express ${expressWalletType} — showing error to user`, { displayedMessage: message });
      setErrorMessage(message);
      onError?.(message);
      setIsSubmitting(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      logExpress('info', `Express ${expressWalletType} — PI succeeded, calling onSuccess (finalize)`, {
        paymentIntentId: paymentIntent.id,
      });
      try {
        await onSuccess?.(paymentIntent.id, expressDetails);
      } catch (finalizeErr) {
        const message =
          finalizeErr && typeof finalizeErr.message === 'string' && finalizeErr.message.trim()
            ? finalizeErr.message
            : 'Your payment was successful but we could not activate your membership yet.';
        logExpress('error', `Express ${expressWalletType} — onSuccess (finalize) threw`, {
          finalizeErrMessage: finalizeErr?.message,
          finalizeErrStatus: finalizeErr?.status,
          finalizeErrCode: finalizeErr?.code,
          displayedMessage: message,
        });
        setErrorMessage(message);
        onError?.(message);
      }
      setIsSubmitting(false);
      return;
    }

    if (paymentIntent) {
      const { status } = paymentIntent;
      let message;
      if (status === 'processing') {
        message =
          'Your payment is still processing. Please wait a moment—you can refresh this page or check your email for confirmation.';
      } else if (status === 'requires_action') {
        message =
          'We could not complete authentication for this payment. Please try again or use a different payment method.';
      } else {
        message = `Payment could not be completed (${status}). Please try again.`;
      }
      logExpress('warn', `Express ${expressWalletType} — PI not succeeded`, { status, displayedMessage: message });
      setErrorMessage(message);
      onError?.(message);
    } else {
      const message = 'Payment could not be completed. Please try again.';
      logExpress('error', `Express ${expressWalletType} — no paymentIntent and no error returned`, { displayedMessage: message });
      setErrorMessage(message);
      onError?.(message);
    }
    setIsSubmitting(false);
  };

  const handleConfirmPayment = () => confirmAndFinalize(null);
  const handleExpressConfirm = (event) => confirmAndFinalize(event);

  const handleCardPaymentSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || isSubmitting || payBlocked) {
      return;
    }

    if (!cardholderName.trim()) {
      const message = 'Please enter the name on the card.';
      setErrorMessage(message);
      onError?.(message);
      return;
    }
    if (!billingPostalCode.trim()) {
      const message = 'Please enter your billing ZIP code.';
      setErrorMessage(message);
      onError?.(message);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const { error } = await elements.submit();
    if (error) {
      const message = error.message || 'Payment details are incomplete.';
      setErrorMessage(message);
      onError?.(message);
      setIsSubmitting(false);
      return;
    }

    await handleConfirmPayment();
  };

  return (
    <form className="SubscriptionUnlimitedElementsCheckout" onSubmit={handleCardPaymentSubmit}>
      <div className="SubscriptionUnlimitedElementsCheckout__main">
        <div className="SubscriptionUnlimitedElementsCheckout__paymentColumn">
          <div className="SubscriptionUnlimitedElementsCheckout__payment">
            <div className="SubscriptionUnlimitedElementsCheckout__paymentTitle">Payment by Card</div>
            <div className="SubscriptionUnlimitedElementsCheckout__paymentSubtitle">
              All transactions are secure and encrypted.
            </div>
            <div className="SubscriptionUnlimitedElementsCheckout__cardInfoLabel">Card information</div>
            <PaymentElement
              options={paymentElementOptions}
              onReady={() => {
                setPaymentElementReady(true);
                logGooglePayExpress('info', 'PaymentElement onReady — mounting ExpressCheckoutElement next (wallets depend on PI + browser + Stripe Dashboard)', {});
              }}
            />

            <div className="SubscriptionUnlimitedElementsCheckout__field">
              <span
                className="SubscriptionUnlimitedElementsCheckout__fieldLabel"
                id="unlimited-payment-cardholder-label"
              >
                Cardholder name
              </span>
              <input
                id="unlimited-payment-cardholder-name"
                type="text"
                autoComplete="cc-name"
                className="SubscriptionUnlimitedElementsCheckout__textFieldInput"
                placeholder="Full name on card"
                aria-labelledby="unlimited-payment-cardholder-label"
                value={cardholderName}
                onChange={(e) => onCardholderNameChange?.(e.target.value)}
              />
            </div>

            <div className="SubscriptionUnlimitedElementsCheckout__field">
              <span
                className="SubscriptionUnlimitedElementsCheckout__fieldLabel"
                id="unlimited-payment-billing-region-label"
              >
                Country or region
              </span>
              <div
                className="SubscriptionUnlimitedElementsCheckout__billingBlock"
                role="group"
                aria-labelledby="unlimited-payment-billing-region-label"
              >
                <div className="SubscriptionUnlimitedElementsCheckout__billingRow">
                  <select
                    id="unlimited-payment-billing-country"
                    className="SubscriptionUnlimitedElementsCheckout__billingSelect"
                    aria-label="Country"
                    value={billingCountryCode}
                    onChange={(e) => onBillingCountryCodeChange?.(e.target.value)}
                  >
                    <option value="US">United States</option>
                  </select>
                </div>
                <div className="SubscriptionUnlimitedElementsCheckout__billingRow">
                  <input
                    id="unlimited-payment-billing-zip"
                    type="text"
                    autoComplete="postal-code"
                    className="SubscriptionUnlimitedElementsCheckout__billingZipInput"
                    aria-label="ZIP code"
                    placeholder="ZIP"
                    value={billingPostalCode}
                    onChange={(e) => onBillingPostalCodeChange?.(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {showExpressSection && paymentElementReady && expressPortalRef?.current
        ? createPortal(
          <div className="SubscriptionUnlimitedElementsCheckout__expressColumn">
            {hasExpressWallets ? (
              <div className="SubscriptionUnlimitedElementsCheckout__expressTitle">Express checkout</div>
            ) : null}

            <div className="SubscriptionUnlimitedElementsCheckout__expressMount">
              <ExpressCheckoutElement
                onReady={(event) => {
                  const methods = event?.availablePaymentMethods ?? {};
                  setExpressLoadError(null);
                  setAvailableWallets(methods);
                  const parsed = parseGooglePayFromExpressMethods(methods);
                  const level = parsed.googlePay ? 'info' : 'warn';
                  logGooglePayExpress(level, parsed.googlePay
                    ? 'ExpressCheckoutElement onReady — Google Pay reported available'
                    : 'ExpressCheckoutElement onReady — Google Pay NOT listed in availablePaymentMethods (check HTTPS, browser, Dashboard Google Pay, PI payment methods)', {
                    availablePaymentMethods: methods,
                    parsed,
                    ...readGooglePaySignalsFromWindow(),
                  });
                  if (parsed.rawKeys.length === 0) {
                    logGooglePayExpress('warn', 'Stripe returned no wallets on this session — Pay with Google will not show. Typical causes: ineligible browser, sandbox/live mismatch, Google Pay off for this Stripe account, or PaymentIntent types exclude wallets.', {
                      ...readGooglePaySignalsFromWindow(),
                    });
                  }
                  if (!parsed.googlePay && !parsed.anyWallet) {
                    logGooglePayExpress('warn', 'No Google Pay or other express wallets — this checkout hides the Express column after resolve, so you will not see wallet buttons at the top.', {
                      parsed,
                      showExpressSectionAfterThis: false,
                    });
                  }
                }}
                onLoadError={(event) => {
                  const msg =
                    event?.error?.message ||
                    event?.message ||
                    'Express Checkout failed to load';
                  setExpressLoadError(msg);
                  setAvailableWallets({});
                  logGooglePayExpress('error', 'ExpressCheckoutElement onLoadError — wallet row failed to initialize', {
                    message: msg,
                    stripeErrorType: event?.error?.type,
                    stripeErrorCode: event?.error?.code,
                    stripeDeclineCode: event?.error?.decline_code,
                    ...readGooglePaySignalsFromWindow(),
                  });
                }}
                onClick={(event) => {
                  event.resolve({
                    emailRequired: true,
                    phoneNumberRequired: true,
                    shippingAddressRequired: true,
                    allowedShippingCountries: ['US'],
                  });
                }}
                onShippingAddressChange={(event) => {
                  const expressCountry = event?.shippingAddress?.address?.country || '';
                  if (expressCountry.trim() && !isExpressCheckoutUsCountry(expressCountry)) {
                    setErrorMessage(NON_US_WALLET_ERROR_MESSAGE);
                    onError?.(NON_US_WALLET_ERROR_MESSAGE);
                    if (typeof event.reject === 'function') {
                      event.reject();
                      return;
                    }
                  }
                  event.resolve({
                    shippingRates: [{
                      id: 'free-shipping',
                      displayName: 'Free shipping',
                      amount: 0,
                    }],
                  });
                }}
                onConfirm={(event) => handleExpressConfirm(event)}
                options={expressOptions}
              />
            </div>

            {hasExpressWallets ? (
              <div className="SubscriptionUnlimitedElementsCheckout__divider" aria-hidden="true">
                <span>or</span>
              </div>
            ) : null}
          </div>,
          expressPortalRef.current,
        )
        : null}

      {errorMessage && (
        <div className="SubscriptionUnlimitedElementsCheckout__error" role="alert">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        className="SubscriptionUnlimitedElementsCheckout__submit"
        disabled={!stripe || !elements || isSubmitting || payBlocked}
      >
        {isSubmitting ? 'Processing...' : 'Pay'}
      </button>
    </form>
  );
};

const SubscriptionUnlimitedElementsCheckout = ({
  clientSecret,
  disabled = false,
  checkoutFormReady = true,
  email,
  billingCountryCode = 'US',
  billingPostalCode = '',
  billingAddressLine1 = '',
  billingAddressLine2 = '',
  billingCity = '',
  billingState = '',
  billingPhoneRaw = '',
  cardholderName = '',
  onBillingCountryCodeChange,
  onBillingPostalCodeChange,
  onCardholderNameChange,
  onError,
  onSuccess,
  returnUrl,
  expressPortalRef,
}) => {
  const stripeAppearanceForUnlimited = useMemo(() => ({
    ...stripeAppearance,
    disableAnimations: true,
    labels: 'above',
    inputs: 'condensed',
    variables: {
      ...stripeAppearance.variables,
      fontFamily: 'Roboto, Albert Sans, sans-serif',
      borderRadius: '6px',
      colorText: 'rgba(26,26,26,0.9)',
      colorTextSecondary: 'rgba(26,26,26,0.7)',
      colorTextPlaceholder: '#777777',
      gridRowSpacing: '8px',
      gridColumnSpacing: '8px',
      colorSuccess: '#D0D0D0',
      accessibleColorOnColorSuccess: '#333333',
      iconCheckmarkColor: '#6F706F',
    },
    rules: {
      ...stripeAppearance.rules,
      '.Label': {
        ...STRIPE_LABEL_TEXT_HIDDEN,
      },
      '.Input': {
        border: '1px solid #E0E0E0',
        boxShadow: '0px 2px 4px rgba(0,0,0,0.07), 0px 1px 1.5px rgba(0,0,0,0.05)',
        backgroundColor: '#FFFFFF',
        borderRadius: '6px',
        padding: '16px 12px',
        color: 'rgba(26,26,26,0.9)',
        outline: 'none',
        transition: 'none',
      },
      '.Input:focus': {
        border: '1px solid #D0D0D0',
        boxShadow: '0px 2px 4px rgba(0,0,0,0.07), 0px 1px 1.5px rgba(0,0,0,0.05)',
        backgroundColor: '#FFFFFF',
        outline: 'none',
        transition: 'none',
      },
      '.Input--invalid': {
        border: '1px solid #FF7F50',
        boxShadow: '0px 2px 4px rgba(0,0,0,0.07), 0px 1px 1.5px rgba(0,0,0,0.05)',
        backgroundColor: '#FFFFFF',
        outline: 'none',
        transition: 'none',
      },
      '.Input--invalid:focus': {
        border: '1px solid #FF7F50',
        boxShadow: '0px 2px 4px rgba(0,0,0,0.07), 0px 1px 1.5px rgba(0,0,0,0.05)',
        outline: 'none',
        transition: 'none',
      },
      '.Block': {
        border: 'none',
        boxShadow: 'none',
        borderRadius: '0',
        overflow: 'visible',
        backgroundColor: 'transparent',
      },
      '.BlockDivider': {
        backgroundColor: 'transparent',
      },
      '.InputDivider': {
        backgroundColor: 'transparent',
      },
      '.AccordionItem': {
        borderColor: '#E0E0E0',
      },
      '.AccordionItem--selected': {
        borderColor: '#E0E0E0',
      },
      '.Tab': {
        border: '1px solid #E0E0E0',
        boxShadow: 'none',
      },
      '.Tab--selected': {
        border: '1px solid #1A1A1A',
        boxShadow: 'none',
      },
    },
  }), []);

  const options = useMemo(() => ({
    clientSecret,
    appearance: stripeAppearanceForUnlimited,
    fonts: stripeFonts,
  }), [clientSecret, stripeAppearanceForUnlimited]);

  if (!clientSecret) {
    return null;
  }

  return (
    <Elements key={clientSecret} stripe={stripePromise} options={options}>
      <ElementsInnerForm
        disabled={disabled}
        checkoutFormReady={checkoutFormReady}
        email={email}
        billingCountryCode={billingCountryCode}
        billingPostalCode={billingPostalCode}
        billingAddressLine1={billingAddressLine1}
        billingAddressLine2={billingAddressLine2}
        billingCity={billingCity}
        billingState={billingState}
        billingPhoneRaw={billingPhoneRaw}
        cardholderName={cardholderName}
        onBillingCountryCodeChange={onBillingCountryCodeChange}
        onBillingPostalCodeChange={onBillingPostalCodeChange}
        onCardholderNameChange={onCardholderNameChange}
        onError={onError}
        onSuccess={onSuccess}
        returnUrl={returnUrl}
        expressPortalRef={expressPortalRef}
      />
    </Elements>
  );
};

export default SubscriptionUnlimitedElementsCheckout;
